import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "useDimensions – a React Hook to measure DOM nodes",
  "description": "",
  "published": "2019-03-13T00:00:00.000Z",
  "redirect_from": ["/blog/usedimensions-a-react-hook-to-measure-dom-nodes/swizec/8983"],
  "categories": "Front End, Technical",
  "hero": "./img/Tm1Bmdw.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Yes it's new, I just `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useDimensions"
      }}>{`open sourced it`}</a>{` 😛`}</p>
    <p>{`The other day I wanted to measure some DOM nodes. This is useful when you have to align items, or respond to browser width, or ... lots of reasons okay.`}</p>
    <p>{`I had to align a curvy line with elements that aren't under my control. This little stepper component uses flexbox to evenly space circles, CSS layouting aligns the title, and you see where this is going.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Doesn't look like much but it's handy for data visualization. Especially when you want to align things with other things.`}<br parentName="p"></br><br parentName="p"></br>{`I used a few of those snippets to position a curved line on this stepper `}<a parentName="p" {...{
          "href": "https://t.co/W9RLizRLPG"
        }}>{`pic.twitter.com/W9RLizRLPG`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1105494223011241984"
      }}>{`March 12, 2019`}</a></blockquote>
    <p>{`SVG in the background detects position of itself, positions of the title and circle, and uses those to define the `}<inlineCode parentName="p">{`start`}</inlineCode>{` and `}<inlineCode parentName="p">{`end`}</inlineCode>{` line of my curve. 👌`}</p>
    <p>{`Many ways you can do this.`}</p>
    <p>{`@lavrton linked to `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/search?q=hook%20size"
      }}>{`a list of existing NPM packages`}</a>{` that sort of do it. @mcalus shared how `}<a parentName="p" {...{
        "href": "https://github.com/mcalus3/open-fraksl/blob/master/src/components/DomainComponents/DrawingComponents/FractalStage.tsx"
      }}>{`he uses `}<inlineCode parentName="a">{`react-sizeme`}</inlineCode></a>{` to get it done.`}</p>
    <p>{`All great, but I wanted something even simpler. I also didn't know about them and kind of just wanted to make my own.`}</p>
    <p>{`Here's an approach I found works great`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Last night I figured out how to measure DOM dimensions with React Hooks. I'm sure it's been covered before but I had fun finding out. `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/200wordsTIL?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#200wordsTIL`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/TmFWZ9Chk0"
        }}>{`pic.twitter.com/TmFWZ9Chk0`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1105494221232910336"
      }}>{`March 12, 2019`}</a></blockquote>
    <h2 {...{
      "id": "usedimensions-hook"
    }}>{`useDimensions hook`}</h2>
    <p>{`This seemed like a neat approach so I turned it into `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useDimensions"
      }}>{`an open source React Hook`}</a>{`. You might enjoy it.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/5b5c3d9367344fb55de7fe20e0f90bae/37048/Tm1Bmdw.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "49.327354260089685%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'197\\'%20viewBox=\\'0%200%20400%20197\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M19%2030v69l1%2068%202%201h356l3-1V30l-181-1-181%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5b5c3d9367344fb55de7fe20e0f90bae/ca0a1/Tm1Bmdw.webp 223w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/75680/Tm1Bmdw.webp 445w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/8d1ba/Tm1Bmdw.webp 890w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/3838e/Tm1Bmdw.webp 1335w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/35b63/Tm1Bmdw.webp 1352w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5b5c3d9367344fb55de7fe20e0f90bae/e92b6/Tm1Bmdw.png 223w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/e66bf/Tm1Bmdw.png 445w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/4ef49/Tm1Bmdw.png 890w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/4e814/Tm1Bmdw.png 1335w", "/static/5b5c3d9367344fb55de7fe20e0f90bae/37048/Tm1Bmdw.png 1352w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/5b5c3d9367344fb55de7fe20e0f90bae/4ef49/Tm1Bmdw.png",
                "alt": "useDimensions source",
                "title": "useDimensions source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`useDimensions source`}</figcaption>{`
  `}</figure></p>
    <p>{`useDimensions source`}</p>
    <p>{`Yep that's it. It really is that simple.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useDimensions"
      }}>{`GitHub link`}</a></p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`useRef`}</inlineCode>{` creates a React.ref, lets you access the DOM`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`useState`}</inlineCode>{` gives you place to store/read the result`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`useLayoutEffect`}</inlineCode>{` runs before browser paint but after all is known`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`getClientBoundingRect()`}</inlineCode>{` measures a DOM node. Width, height, x, y, etc`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`toJSON`}</inlineCode>{` turns a DOMRect object into a plain object so you can destructure`}</li>
    </ul>
    <p>{`Here's how to use it in your project 👇`}</p>
    <p>{`First, add `}<inlineCode parentName="p">{`useDimensions`}</inlineCode>{` to your project`}</p>
    <pre><code parentName="pre" {...{}}>{`$ yarn add react-use-dimensions
or
$ npm install --save react-use-dimensions
`}</code></pre>
    <p>{`Using it in a component looks like this`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">import React from 'react';
import useDimensions from 'react-use-dimensions';

const MyComponent = () => {
    const [ref, { x, y, width }] = useDimensions();

    return (
        <div ref={ref}>
            This is the element you'll measure
        </div>
    )
}
`}</code></pre>
    <p><inlineCode parentName="p">{`useDimensions`}</inlineCode>{` returns a 2-element array. First the `}<inlineCode parentName="p">{`ref`}</inlineCode>{`, second the dimensions.`}</p>
    <p>{`This is so multiple `}<inlineCode parentName="p">{`useDimensions`}</inlineCode>{` hooks in the same component don't step on each others' toes. Create as many refs and measurement objects as you'd like.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">const MyComponent = () => {
    const [stepRef, stepSize] = useDimensions();
    const [titleRef, titleSize] = useDimensions();

    console.log("Step is at X: ", stepSize.x);
    console.log("Title is", titleSize.width, "wide");

    return (
        <div>
            <div ref={stepRef}>This is a step</div>
            <h1 ref={titleRef}>The title</h1>
        </div>
    )
}
`}</code></pre>
    <p>{`MIT License of course.`}</p>
    <p>{`Consider retweeting if you think it's neat`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`🔥useDimensions is now an open source react hook 🔥`}<br parentName="p"></br><br parentName="p"></br>{`🐙 GitHub --> `}<a parentName="p" {...{
          "href": "https://t.co/CNPj4RVfOw"
        }}>{`https://t.co/CNPj4RVfOw`}</a><br parentName="p"></br>{`✍ short blog --> `}<a parentName="p" {...{
          "href": "https://t.co/S5Wp6PJX95"
        }}>{`https://t.co/S5Wp6PJX95`}</a><br parentName="p"></br><br parentName="p"></br>{`Simple and works great 👌 `}<a parentName="p" {...{
          "href": "https://t.co/8ZWYUCUhIe"
        }}>{`pic.twitter.com/8ZWYUCUhIe`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1105868919129030656"
      }}>{`March 13, 2019`}</a></blockquote>
    <p>{`Enjoy ✌️ ~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      